import { useTracking } from '@/composables/useTracking';
import { useRoute } from 'vue-router';
import type { APITrackingData } from '@/types/api';

export function useAPITracking() {
  const { track } = useTracking();
  
  // 安全地获取路由信息，避免在非Vue组件环境中出错
  let route: any = null;
  try {
    route = useRoute();
  } catch (error) {
    // 在非Vue组件环境中，route可能不可用
    console.warn('⚠️ 无法获取路由信息，将使用window.location.pathname');
  }

  /**
   * 手动埋点API请求
   * @param data API请求埋点数据
   */
  const trackAPIRequest = (data: APITrackingData) => {
    try {
      // 自动补充页面路径，添加安全检查
      const currentPath = route?.path || window.location.pathname || '';
      const trackingData: APITrackingData = {
        ...data,
        page_path: data.page_path || currentPath
      };
      
      // 调试信息（生产环境可移除）
      // console.log('🔍 API埋点调试信息:', {
      //   originalData: data,
      //   routePath: route?.path,
      //   windowPath: window.location.pathname,
      //   finalPath: trackingData.page_path
      // });
      
      track('tech_api_status', trackingData);
      // console.log('✅ API埋点成功:', trackingData);
    } catch (error) {
      console.error('❌ API埋点失败:', error);
      // 尝试使用备用方案
      try {
        const fallbackData: APITrackingData = {
          ...data,
          page_path: data.page_path || window.location.pathname || '/unknown'
        };
        track('tech_api_status', fallbackData);
        // console.log('🔄 使用备用方案埋点成功:', fallbackData);
      } catch (fallbackError) {
        console.error('❌ 备用方案也失败了:', fallbackError);
      }
    }
  };

  /**
   * 为axios实例添加拦截器（自动埋点）
   * @param axiosInstance axios实例
   */
  const createAxiosInterceptor = (axiosInstance: any) => {
    // 请求拦截器
    axiosInstance.interceptors.request.use(
      (config: any) => {
        // 记录请求开始时间
        config.metadata = { startTime: Date.now() };
        
        // 计算请求大小
        let requestSize = 0;
        if (config.data) {
          if (typeof config.data === 'string') {
            requestSize = new Blob([config.data]).size;
          } else {
            requestSize = new Blob([JSON.stringify(config.data)]).size;
          }
        }
        config.metadata.requestSize = requestSize;
        
        return config;
      },
      (error: any) => {
        return Promise.reject(error);
      }
    );

    // 响应拦截器
    axiosInstance.interceptors.response.use(
      (response: any) => {
        const endTime = Date.now();
        const startTime = response.config.metadata?.startTime || endTime;
        const duration = endTime - startTime;
        const requestSize = response.config.metadata?.requestSize || 0;
        
        // 获取响应数据中的code
        let resultCode = '';
        try {
          if (response.data && typeof response.data === 'object') {
            resultCode = response.data.code?.toString() || '';
          }
        } catch (e) {
          // 忽略解析错误
        }
        
        // 自动埋点
        trackAPIRequest({
          api_name: response.config.url || '',
          status_code: response.status.toString(),
          duration,
          result_code: resultCode,
          method: response.config.method?.toUpperCase(),
          request_size: requestSize
        });
        
        return response;
      },
      (error: any) => {
        const endTime = Date.now();
        const startTime = error.config?.metadata?.startTime || endTime;
        const duration = endTime - startTime;
        const requestSize = error.config?.metadata?.requestSize || 0;
        
        // 获取错误状态码
        let statusCode = '0';
        if (error.response) {
          statusCode = error.response.status.toString();
        } else if (error.code) {
          statusCode = error.code.toString();
        }
        
        // 自动埋点（错误情况）
        trackAPIRequest({
          api_name: error.config?.url || '',
          status_code: statusCode,
          duration,
          result_code: '',
          method: error.config?.method?.toUpperCase(),
          request_size: requestSize
        });
        
        return Promise.reject(error);
      }
    );
  };

  /**
   * 为fetch添加拦截器（自动埋点）
   */
  const createFetchInterceptor = () => {
    const originalFetch = window.fetch;
    
    window.fetch = async function(...args) {
      const startTime = Date.now();
      const url = typeof args[0] === 'string' ? args[0] : (args[0] as Request).url;
      const method = typeof args[1] === 'object' && args[1]?.method ? args[1].method.toUpperCase() : 'GET';
      
      // 计算请求大小
      let requestSize = 0;
      if (args[1]?.body) {
        if (typeof args[1].body === 'string') {
          requestSize = new Blob([args[1].body]).size;
        } else if (args[1].body instanceof FormData) {
          // FormData大小计算比较复杂，这里简化处理
          requestSize = 0;
        } else {
          requestSize = new Blob([JSON.stringify(args[1].body)]).size;
        }
      }
      
      try {
        const response = await originalFetch.apply(this, args);
        const endTime = Date.now();
        const duration = endTime - startTime;
        
        // 克隆响应以读取body
        const clonedResponse = response.clone();
        let resultCode = '';
        
        try {
          const responseText = await clonedResponse.text();
          if (responseText) {
            const responseData = JSON.parse(responseText);
            resultCode = responseData.code?.toString() || '';
          }
        } catch (e) {
          // 忽略解析错误
        }
        
        // 自动埋点
        trackAPIRequest({
          api_name: url,
          status_code: response.status.toString(),
          duration,
          result_code: resultCode,
          method,
          request_size: requestSize
        });
        
        return response;
      } catch (error) {
        const endTime = Date.now();
        const duration = endTime - startTime;
        
        // 自动埋点（错误情况）
        trackAPIRequest({
          api_name: url,
          status_code: '0',
          duration,
          result_code: '',
          method,
          request_size: requestSize
        });
        
        throw error;
      }
    };
  };

  return {
    trackAPIRequest,
    createAxiosInterceptor,
    createFetchInterceptor
  };
}
